import Button from "@cloudscape-design/components/button"
import ButtonDropdown from "@cloudscape-design/components/button-dropdown"
import Checkbox from "@cloudscape-design/components/checkbox"
import Icon from "@cloudscape-design/components/icon"
import Input from "@cloudscape-design/components/input"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { Fragment } from "react"
import InlineEditableText from "../../../components/InlineEditableText"
import { ProtocolAssessment } from "../../../services/studyProtocolApi"

interface AssessmentWithIndex extends ProtocolAssessment {
  idx: number
}

interface VisitInfo {
  id: string
  periodName: string
}

interface CategorySectionProps {
  /**
   * The category name being rendered.
   */
  category: string

  /**
   * Assessments belonging to this category (with their original indices)
   */
  categoryAssessments: AssessmentWithIndex[]

  /**
   * All visits across all periods for rendering checkbox columns.
   */
  allVisits: VisitInfo[]

  /**
   * Height of the thead for sticky positioning.
   */
  theadHeight: number

  /**
   * Whether this category is currently being edited (name change)
   */
  editingCategory: boolean

  /**
   * Whether this is a newly created category awaiting rename.
   */
  isNewCategory: boolean

  /**
   * Category currently accepting a new assessment (null if none)
   */
  addingAssessment: string | null

  /**
   * Current value of the new assessment name input.
   */
  newAssessmentName: string

  /**
   * ID of the visit column currently being hovered.
   */
  hoveringVisit: string | null

  /**
   * When true, disables all editing interactions.
   */
  readOnly?: boolean

  /**
   * Callbacks for category operations.
   */
  onEditCategory: (editing: boolean) => void
  onSaveCategory: (newName: string) => void
  onCancelEditCategory: () => void
  onDeleteCategory: () => void

  /**
   * Callbacks for assessment operations.
   */
  onStartAddingAssessment: () => void
  onNewAssessmentNameChange: (name: string) => void
  onAddAssessment: () => void
  onCancelAddAssessment: () => void
  onUpdateAssessment: (idx: number, field: keyof ProtocolAssessment, value: string) => void
  onDeleteAssessment: (idx: number) => void

  /**
   * Callbacks for visit checkbox operations.
   */
  onToggleVisit: (assessmentIdx: number, visitPosition: number) => void
  onHoverVisit: (visitId: string | null) => void
}

/**
 * Renders a category section within the assessments grid.
 *
 * Includes the category header row with inline editing and menu, all assessment rows with
 * checkboxes for each visit, and the "add assessment" input row when active.
 */
const CategorySection = ({
  category,
  categoryAssessments,
  allVisits,
  theadHeight,
  editingCategory,
  isNewCategory,
  addingAssessment,
  newAssessmentName,
  hoveringVisit,
  readOnly = false,
  onEditCategory,
  onSaveCategory,
  onCancelEditCategory,
  onDeleteCategory,
  onStartAddingAssessment,
  onNewAssessmentNameChange,
  onAddAssessment,
  onCancelAddAssessment,
  onUpdateAssessment,
  onDeleteAssessment,
  onToggleVisit,
  onHoverVisit,
}: CategorySectionProps) => {
  return (
    <Fragment>
      {/* Category header row */}
      <tr className="category-row" style={{ top: theadHeight }}>
        <td colSpan={allVisits.length + 1} className="category-cell">
          {readOnly ? (
            <span className="category-name readonly">{category}</span>
          ) : (
            <>
              <InlineEditableText
                value={category}
                editing={editingCategory || isNewCategory}
                onEditingChange={onEditCategory}
                onSave={onSaveCategory}
                onCancel={onCancelEditCategory}
                className="category-name"
              />
              <div className="category-actions">
                <ButtonDropdown
                  variant="icon"
                  expandToViewport
                  ariaLabel={`${category} category actions`}
                  items={[
                    { id: "add-assessment", text: "Add Assessment", iconName: "add-plus" },
                    { id: "remove-category", text: "Remove Category", iconName: "remove" },
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === "add-assessment") onStartAddingAssessment()
                    else if (detail.id === "remove-category") onDeleteCategory()
                  }}
                />
              </div>
            </>
          )}
        </td>
      </tr>

      {/* Assessment rows */}
      {categoryAssessments.map((assessment) => (
        <tr key={assessment.idx} className="assessment-row">
          <td className="row-label-col">
            {readOnly ? (
              <div className="assessment-name-cell">
                <span className="assessment-name-text">{assessment.name}</span>
              </div>
            ) : (
              <div className="assessment-name-cell">
                <InlineEditableText
                  value={assessment.name}
                  multiline
                  onSave={(newName) => onUpdateAssessment(assessment.idx, "name", newName)}
                  className="assessment-name-text"
                />
                <ButtonDropdown
                  variant="icon"
                  expandToViewport
                  ariaLabel={`${assessment.name} assessment actions`}
                  items={[{ id: "remove", text: "Remove Assessment", iconName: "remove" }]}
                  onItemClick={() => onDeleteAssessment(assessment.idx)}
                />
              </div>
            )}
          </td>

          {allVisits.map((visit, visitIndex) => {
            const visitPosition = visitIndex + 1
            const isChecked = assessment.scheduled_visits?.includes(visitPosition) || false
            if (readOnly) {
              return (
                <td key={visit.id} className="checkbox-cell readonly">
                  {isChecked ? (
                    <span
                      className="checkbox-cell-check"
                      role="img"
                      aria-label={`Scheduled at ${visit.periodName}`}
                    >
                      <Icon name="check" variant="success" size="small" />
                    </span>
                  ) : (
                    <span className="checkbox-cell-empty" aria-label="Not scheduled" role="img" />
                  )}
                </td>
              )
            }
            return (
              <td
                key={visit.id}
                className={`checkbox-cell ${hoveringVisit === visit.id ? "hovering" : ""}`}
                onClick={() => onToggleVisit(assessment.idx, visitPosition)}
                onMouseEnter={() => onHoverVisit(visit.id)}
                onMouseLeave={() => onHoverVisit(null)}
              >
                <span
                  className="checkbox-cell-control"
                  onClick={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={() => onToggleVisit(assessment.idx, visitPosition)}
                    ariaLabel={`Scheduled at ${visit.periodName}`}
                  />
                </span>
              </td>
            )
          })}
        </tr>
      ))}

      {/* Add assessment row */}
      {!readOnly && addingAssessment === category && (
        <tr>
          <td className="row-label-col">
            <Input
              value={newAssessmentName}
              onChange={({ detail }) => onNewAssessmentNameChange(detail.value)}
              placeholder="Assessment name"
              autoFocus
              onKeyDown={(event) => {
                if (event.detail.key === "Enter") onAddAssessment()
                if (event.detail.key === "Escape") onCancelAddAssessment()
              }}
            />
          </td>
          <td colSpan={allVisits.length}>
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="primary" onClick={onAddAssessment}>
                Add
              </Button>
              <Button onClick={onCancelAddAssessment}>Cancel</Button>
            </SpaceBetween>
          </td>
        </tr>
      )}
    </Fragment>
  )
}

export default CategorySection
